import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import {
  AdminPageTitle,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminSupportingText,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  CONFIG_LIST_ACTION_LABELS,
  MESSAGES,
  SUPPORT_TICKET_CONFIG_ROUTE,
  buildSampleSupportTicketConfigurations,
  formatSupportTicketConfigDate,
  getAvailableForSummary,
  getEntryPointsSummary,
  getListActionsForConfiguration,
} from '../../../data/supportTicketConfig';

const tableCols =
  'minmax(96px, 0.7fr) minmax(100px, 0.75fr) minmax(140px, 1.15fr) minmax(140px, 1.1fr) minmax(180px, 1.35fr) minmax(88px, 0.65fr) minmax(120px, 0.9fr) minmax(120px, 0.9fr) minmax(48px, 0.2fr)';

const TABLE_MIN_WIDTH = '1280px';

const SupportTicketConfigurations = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [configurations, setConfigurations] = useState(() =>
    buildSampleSupportTicketConfigurations(),
  );
  const [feedback, setFeedback] = useState('');

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  useEffect(() => {
    const incoming = location.state?.configurations;
    const message = location.state?.feedback;
    if (Array.isArray(incoming) || message) {
      if (Array.isArray(incoming)) setConfigurations(incoming);
      if (message) setFeedback(message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRows,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(configurations);

  const openView = (row) => {
    navigate(`${SUPPORT_TICKET_CONFIG_ROUTE}/${encodeURIComponent(row.id)}`, {
      state: { configuration: row, configurations },
    });
  };

  const openEdit = (row) => {
    navigate(`${SUPPORT_TICKET_CONFIG_ROUTE}/${encodeURIComponent(row.id)}/edit`, {
      state: { configuration: row, configurations },
    });
  };

  const handleRowAction = (action, row) => {
    if (action === 'view') openView(row);
    else if (action === 'edit') openEdit(row);
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div
          className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <AdminPageTitle className="!mb-0">Support Ticket</AdminPageTitle>
        </div>

        {feedback ? (
          <AdminSupportingText className="text-primaryblue mb-4 px-4">{feedback}</AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div
              className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
            >
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Rule ID</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Module</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Ticket Type</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Available For</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Entry Points</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Priority</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Date Added</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Last Updated</AdminTableHeader>
              </div>
              <div className="flex items-center shrink-0 justify-center">
                <AdminTableHeader>Actions</AdminTableHeader>
              </div>
            </div>

            {paginatedRows.length === 0 ? (
              <div className="px-4 py-8">
                <AdminSupportingText className="text-mediumgray text-center">
                  {MESSAGES.empty}
                </AdminSupportingText>
              </div>
            ) : null}

            {paginatedRows.map((row) => {
              const actions = getListActionsForConfiguration(row);
              const availableFor = getAvailableForSummary(row);
              const entryPoints = getEntryPointsSummary(row);
              return (
                <div
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openView(row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openView(row);
                    }
                  }}
                  className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                  style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                >
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableMeta className="truncate block" title={row.id}>
                      {row.id}
                    </AdminTableMeta>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={row.module}>
                      {row.module}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={row.ticketType}>
                      {row.ticketType}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={availableFor}>
                      {availableFor}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={entryPoints}>
                      {entryPoints}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">{row.automaticPriority}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={formatSupportTicketConfigDate(row.dateAdded)}>
                      {formatSupportTicketConfigDate(row.dateAdded)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={formatSupportTicketConfigDate(row.lastUpdated)}>
                      {formatSupportTicketConfigDate(row.lastUpdated)}
                    </AdminTableBody>
                  </div>
                  <div
                    className="flex items-center justify-center shrink-0 overflow-visible"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="relative inline-flex"
                      ref={(el) => {
                        rowActionsRefs.current[row.id] = el;
                      }}
                    >
                      <AdminRowActionsButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowActions(row.id);
                        }}
                        aria-expanded={!!rowActionsOpen[row.id]}
                        title="Actions"
                        aria-label="Row actions"
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                      {rowActionsOpen[row.id] && rowActionsMenuLayout ? (
                        <AdminFixedRowActionsMenu
                          layout={rowActionsMenuLayout}
                          menuRef={rowActionsMenuRef}
                          placementReady={rowActionsMenuPlacementReady}
                        >
                          {actions.map((action) => (
                            <AdminRowMenuItem
                              key={action}
                              onClick={(e) => {
                                e.stopPropagation();
                                closeRowActions();
                                handleRowAction(action, row);
                              }}
                            >
                              {CONFIG_LIST_ACTION_LABELS[action]}
                            </AdminRowMenuItem>
                          ))}
                        </AdminFixedRowActionsMenu>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={configurations.length > 0 && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default SupportTicketConfigurations;
